import React, { useState } from 'react';
import { marathiLabels, categoryIcons, colorClasses, filterOrder } from '../utils/categoryStyles'; // Import filterOrder

const FilterPanel = ({ filters, setFilters, resources, allFilterOptions, resourceFieldMap }) => {
  // Collapsed state for each category
  const [collapsed, setCollapsed] = useState({});

  // Helper to toggle a section
  const toggleCategory = (category) => {
    setCollapsed(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Helper to get count of resources for a filter option
  const getOptionCount = (category, option) => {
    // `option` is from allFilterOptions (e.g., "Game", "Medium", "English Numerals")
    // `filters` state stores lowercase, trimmed values (e.g., "game", "medium", "english numerals")
    const lowerOption = option.toLowerCase();

    return resources.filter(res => {
      const field = resourceFieldMap[category];
      let resourceValue = res[field];

      if (category === 'digits') { // Changed from 'language'
        resourceValue = resourceValue === 'English' ? 'English Numerals' :
                        resourceValue === 'Marathi' ? 'Devnagiri Numerals' :
                        resourceValue;
      }
      // Check 1: Does the current resource's value for this category match the 'option' being counted?
      if (!resourceValue || resourceValue.trim().toLowerCase() !== lowerOption) {
        return false;
      }

            // Check 2: Does this resource also satisfy all *other* active filters?
      // `vals` from `filters` state are already lowercase and trimmed.
      return Object.entries(filters).every(([otherCatKey, otherCatVals]) => {
        if (otherCatKey === category || otherCatVals.length === 0) return true; // Skip self or empty filters

        const otherResourceField = resourceFieldMap[otherCatKey];
        let otherResourceValue = res[otherResourceField];

        if (otherCatKey === 'digits') { // Changed from 'language'
          // For language, `otherCatVals` are like ["english numerals"] (from lowercase filters state).
          // `res.language` (otherResourceValue) is "English".
          // This simplified comparison assumes `otherCatVals` are directly comparable to transformed `otherResourceValue`.
          // A more accurate count for cross-filter effects involving language would require `normalizeLang` from App.js.
          const transformedOtherResourceValue = (otherResourceValue === 'English' ? 'English Numerals' :
                                                 otherResourceValue === 'Marathi' ? 'Devnagiri Numerals' :
                                                 otherResourceValue)?.trim().toLowerCase();
          return otherCatVals.some(v => v === transformedOtherResourceValue);
        } else {
          // For non-language filters, `otherCatVals` are lowercase (e.g. ['maths'])
          // `otherResourceValue` is mixed case (e.g. "Maths")
          return otherCatVals.some(v => v === otherResourceValue?.trim().toLowerCase());
        }
      });
    }).length;
  };

  // Helper to sort options
  const getSortedOptions = (category, options) => {
    const order = filterOrder.options[category];
    if (order) {
      // Sort based on the predefined order, placing items not in the order at the end
      return [...options].sort((a, b) => {
        const aLower = a.toLowerCase().replace(/\s+/g, category === 'type' ? '-' : '_');
        const bLower = b.toLowerCase().replace(/\s+/g, category === 'type' ? '-' : '_');
        const aIndex = order.indexOf(aLower);
        const bIndex = order.indexOf(bLower);
        if (aIndex === -1 && bIndex === -1) return 0; // Both not in order, keep original relative order
        if (aIndex === -1) return 1; // a not in order, b is - a goes after b
        if (bIndex === -1) return -1; // b not in order, a is - b goes after a
        return aIndex - bIndex; // Both in order, sort by index
      });
    }
    return options;
  };

  // Handle checkbox change
  const handleFilterChange = (category, option, isChecked) => {
    const lowerOption = option.toLowerCase(); // Ensure we work with lowercase
    setFilters(prev => {
      const currentValues = prev[category] || []; // Current filters for this category (should be lowercase)
      let newValues;
      if (isChecked) {
        // Add if not already present
        newValues = currentValues.includes(lowerOption) ? currentValues : [...currentValues, lowerOption];
      } else {
        // Remove
        newValues = currentValues.filter(val => val !== lowerOption); // val is already lowercase
      }
      return { ...prev, [category]: newValues };
    });
  };

  return (
    <div className="border rounded-lg p-3 sm:p-4 shadow-md ring-1 ring-gray-100 bg-white text-sm">
      {/* Iterate through categories based on the preferred order */}
      {filterOrder.categories.map(category => {
        const currentCategoryOptions = allFilterOptions[category];
        if (!currentCategoryOptions || currentCategoryOptions.length === 0) return null;

        const sortedOptions = getSortedOptions(category, currentCategoryOptions);
        const catIcon = categoryIcons[category]?.icon;
        const color = categoryIcons[category]?.color || 'gray';
        const catColorClass = colorClasses[color] || colorClasses.gray;
        let headerLabel = category.charAt(0).toUpperCase() + category.slice(1);
        if (category === 'digits') headerLabel = 'Numeral System';
        if (category === 'subtopic') headerLabel = 'Topic';

        return (
          <div key={category} className="pb-3 mb-3 border-b border-gray-200 last:border-b-0 last:pb-0 last:mb-0">
            {/* Collapsible header */}
            <h4
              className="flex items-center font-semibold mb-2 text-gray-800 cursor-pointer select-none"
              onClick={() => toggleCategory(category)}
              tabIndex={0}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggleCategory(category)}
              aria-expanded={!collapsed[category]}
              aria-controls={`filter-options-${category}`}
            >
              <span className={`inline-flex items-center gap-1.5 ${catColorClass} px-2.5 py-1 rounded-full text-xs`}>
                {catIcon && React.cloneElement(catIcon, { className: "w-3.5 h-3.5" })}
                {headerLabel}
              </span>
              <svg
                className={`ml-2 w-4 h-4 transition-transform duration-200 ${collapsed[category] ? 'rotate-[-90deg]' : 'rotate-0'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9l6 6 6-6" />
              </svg>
            </h4>
            {/* Collapsible options */}
            <div
              id={`filter-options-${category}`}
              className={`space-y-1 pl-1 transition-all duration-200 ease-in-out overflow-hidden`}
              style={{
                maxHeight: collapsed[category] ? 0 : 500,
                opacity: collapsed[category] ? 0 : 1,
                pointerEvents: collapsed[category] ? 'none' : 'auto'
              }}
            >
              {sortedOptions.map(option => {
                const count = getOptionCount(category, option);
                const isSelected = filters[category]?.some(val => val === option.toLowerCase());
                const isDisabled = resources.length > 0 && count === 0 && !isSelected;
                let optionKey = option.toLowerCase().replace(/\s+/g, '_');
                if (category === 'type') {
                  optionKey = option.toLowerCase().replace(/\s+/g, '-');
                }
                const optionIcon = categoryIcons[category]?.items?.[optionKey]?.icon || categoryIcons[category]?.items?.default?.icon;
                const label = marathiLabels[optionKey] || option;
                return (
                  <label
                    key={option}
                    className={`flex items-center justify-between py-1 px-2 rounded-md transition-colors
                      ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-blue-50 cursor-pointer'}
                      ${isSelected && !isDisabled ? 'bg-blue-100 text-blue-800 font-semibold' : 'text-gray-800'}
                      text-sm`}
                    style={{ minHeight: 36 }}
                  >
                    <div className="flex items-center w-full">
                      <input
                        type="checkbox"
                        className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 focus:ring-1 sm:focus:ring-2
                          ${isDisabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={(e) => !isDisabled && handleFilterChange(category, option, e.target.checked)}
                        tabIndex={-1}
                      />
                      <span className="ml-2 flex items-center gap-2 w-full">
                        {optionIcon && React.cloneElement(optionIcon, { className: "w-4 h-4 flex-shrink-0" })}
                        <span className="truncate" title={label}>{label}</span>
                      </span>
                    </div>
                    <span className={`text-xs ml-2 ${isSelected && !isDisabled ? 'text-blue-700' : isDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                      ({count})
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FilterPanel;
